const POSSIBLE_CLIENT_IDENTIFIERS = ['x-app'];
const WEB_VIEW_CAPABLE_CLIENT_IDENTIFIERS = ['x-app'];
const HAS_NATIVE_BACK_BUTTON = ['x-app'];
const SUPPORTS_NATIVE_AUTH = ['x-app'];

type Query = Record<string, boolean | string>;

export type ClientIdentifier = {
  name: string | undefined;
  version: string | undefined;
};

export enum ClientFeature {
  Modal = 'modal',
  NativeModals = 'nativeModals',
}

export type ClientCapabilities = ClientIdentifier & {
  is: (_identifier: ClientIdentifier | string) => boolean;
  isWebViewCapable: () => boolean;
  supportsNativeAuth: () => boolean;
  hasNativeBackButton: () => boolean;
  hasFeature: (
    _feature: ClientFeature | string,
    _defaultValue?: boolean,
  ) => boolean;
  toString: () => string;
};

export const getClientIdentifierFromUserAgentString = (
  userAgent: string,
): ClientIdentifier => {
  const identifier = getClientsFromUserAgent(userAgent).reduce<
    ClientIdentifier | undefined
  >((matched, identifier) => {
    if (typeof matched !== 'undefined') {
      return matched;
    }

    if (POSSIBLE_CLIENT_IDENTIFIERS.includes(identifier.name)) {
      return identifier;
    }

    return matched;
  }, undefined);

  return (
    identifier ?? {
      name: undefined,
      version: undefined,
    }
  );
};

export const clientIdentifierToCapabilities = (
  identifier: ClientIdentifier,
): ClientCapabilities => {
  const { name, version: versionWithQueryString } = identifier ?? {};

  const { version, queryString } = getSeparatedVersionAndQuery(
    versionWithQueryString,
  );

  let parsedQuery: Query | undefined = undefined;
  const getQuery = (): Query => {
    if (typeof parsedQuery !== 'undefined') {
      return parsedQuery;
    }

    if (typeof queryString === 'undefined') {
      return (parsedQuery = {});
    }

    return (parsedQuery = parseQueryString(queryString));
  };

  return {
    name,
    version,
    is: other => {
      if (typeof name === 'undefined') {
        return false;
      }

      if (typeof other === 'string') {
        return other === name;
      }

      return other.name === name;
    },
    isWebViewCapable: () => {
      if (typeof name === 'undefined') {
        return false;
      }

      return WEB_VIEW_CAPABLE_CLIENT_IDENTIFIERS.includes(name);
    },
    hasNativeBackButton: () => {
      if (typeof name === 'undefined') {
        return false;
      }

      return HAS_NATIVE_BACK_BUTTON.includes(name);
    },
    supportsNativeAuth: () => {
      if (typeof name === 'undefined') {
        return false;
      }

      return SUPPORTS_NATIVE_AUTH.includes(name);
    },
    hasFeature: (feature: string, defaultValue: boolean = false) => {
      const query = getQuery();

      if (!(feature in query)) {
        return defaultValue;
      }

      return query[feature] === true;
    },
    toString: () => {
      const identifierAsString = [identifier?.name, version]
        .filter(part => !!part)
        .join('/');

      if (typeof queryString === 'undefined') {
        return identifierAsString;
      }

      return `${identifierAsString}?${queryString}`;
    },
  };
};

export const getClientsFromUserAgent = (agent: string) => {
  const pattern = /\b([^/\s]+)\/([^/\s]+)\b/g;
  // \b = word boundary
  // ([^/\s]+) = capture 1
  // / = literal slash
  // ([^/\s]+) = capture 2
  // g = "global" to find all occurrences

  const matches = [];
  let match;

  while ((match = pattern.exec(agent)) !== null) {
    matches.push({
      name: match[1].trim(),
      version: match[2].trim(),
    });
  }

  return matches;
};

const parseQueryString = (queryString: string): Query => {
  const parsed: Query = {};

  new URLSearchParams(queryString).forEach((value, name) => {
    if (typeof name === 'undefined') {
      return;
    }
    if (
      typeof value === 'undefined' ||
      value === null ||
      value === '' ||
      value === '1' ||
      value === 'true'
    ) {
      parsed[name] = true;
      return;
    }

    if (value === '0' || value === 'false') {
      parsed[name] = false;
      return;
    }

    parsed[name] = value.toString();
  });

  return parsed;
};

const getSeparatedVersionAndQuery = (
  versionWithQueryString: string | undefined,
) => {
  if (typeof versionWithQueryString === 'undefined') {
    return {
      version: undefined,
      queryString: undefined,
    };
  }

  const [version = undefined, rawQueryString = ''] =
    versionWithQueryString.split('?');

  const queryString = rawQueryString.trim();

  if (queryString.length === 0) {
    return {
      version: version,
      queryString: undefined,
    };
  }

  return {
    version: version,
    queryString: queryString,
  };
};
